import React from 'react';
import { VfxSpan } from '../utils/helpers';
import { LOOTBOX_RARITIES } from '../constants/lootbox';
import ItemIcon from './lootbox/ItemIcon';

const moneyBagStyle = { width: '18px', height: '18px', verticalAlign: 'middle', marginLeft: '4px' };

function PlayerNameWithEmoji({ playerName, playerId, playerEmojis, seasonConfig }) {
  // Check if this player was the richest from the previous season
  const currentSeason = seasonConfig?.currentSeason || 1;
  const prevSeasonData = seasonConfig?.seasons?.[String(currentSeason - 1)];
  const isRichest = prevSeasonData?.richestPlayer?.claimedPlayer === playerId;
  const moneyBag = isRichest ? <img src="/moneybag.svg" alt="Richest Player" title={`S${currentSeason - 1} Richest Player`} style={moneyBagStyle} /> : null;

  const emojiData = playerEmojis[playerId];
  if (emojiData) {
    const isShiny = emojiData.isShiny || false;
    const isShinyGalaxy = isShiny && emojiData.rarity === 'GALAXY';
    const isNuclear = emojiData.rarity === 'NUCLEAR';
    const vfx = emojiData.vfx || null;

    // Render emoji icon using ItemIcon (supports SVG art for rank rewards etc.)
    const emojiEl = <ItemIcon item={emojiData} size={18} style={{ verticalAlign: 'middle' }} />;

    // VFX wrapper: wraps both icon and name with the shader effect
    const wrapWithVfx = (content, extraStyle) => {
      if (!vfx) return <span style={extraStyle}>{content}</span>;
      return <VfxSpan vfx={vfx} style={{ display: 'inline-block', ...extraStyle }}>{content}</VfxSpan>;
    };

    if (isNuclear) {
      // Nuclear effect with radioactive glow, pulse, and shake
      const nameStyle = {
        fontWeight: 'bold',
        position: 'relative',
        display: 'inline-block',
        animation: 'nuclearLeaderboard 1.5s ease-in-out infinite',
        textShadow: `
          0 0 10px #00ff00,
          0 0 20px #00ff00,
          0 0 30px #00ff00,
          0 0 40px #39ff14,
          0 0 50px #39ff14,
          0 0 60px #39ff14,
          0 0 70px #39ff14,
          0 0 80px #00ff00,
          0 0 100px rgba(0, 255, 0, 0.5)
        `,
        filter: 'brightness(1.3) contrast(1.4) saturate(1.5)',
        letterSpacing: '1px'
      };
      return wrapWithVfx(
        <span className="nuclear-player-name" style={nameStyle}>
          {emojiEl} {playerName} {emojiEl}{moneyBag}
        </span>
      );
    } else if (isShinyGalaxy) {
      // Special RGB rainbow glow for shiny Galaxy items - ENHANCED
      const nameStyle = {
        fontWeight: 'bold',
        display: 'inline-block',
        animation: 'rainbowPulse 2s ease-in-out infinite',
        filter: 'brightness(1.3) saturate(1.4)',
        textShadow: `
          0 0 15px rgba(255, 255, 255, 0.8),
          0 0 30px rgba(255, 255, 255, 0.6),
          0 0 45px rgba(255, 255, 255, 0.4)
        `
      };
      return wrapWithVfx(
        <span className="rainbow-glow" style={nameStyle}>
          {emojiEl} {playerName} {emojiEl}{moneyBag}
        </span>
      );
    } else if (isShiny) {
      // Get the glow color based on the emoji's rarity
      const rarityData = LOOTBOX_RARITIES[emojiData.rarity] || LOOTBOX_RARITIES.COMMON;
      const glowColor = rarityData.color;
      const rarity = emojiData.rarity;

      // Convert hex to RGB for rgba usage
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 255, g: 215, b: 0 }; // Default to gold
      };

      const rgb = hexToRgb(glowColor);

      // Enhanced effects for high-tier shinies (Legendary, Mythic, Epic)
      const isHighTier = rarity === 'LEGENDARY' || rarity === 'MYTHIC';
      const isMidTier = rarity === 'EPIC';
      const glowIntensity = isHighTier ? 0.9 : (isMidTier ? 0.7 : 0.6);
      const extraLayers = isHighTier ? `,
        0 0 40px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4),
        0 0 50px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3),
        0 0 60px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2),
        0 0 70px rgba(255, 255, 255, 0.3),
        0 0 80px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)
      ` : '';

      // Determine brightness based on tier
      let brightnessFilter = 'brightness(1.1)';
      if (isHighTier) {
        brightnessFilter = 'brightness(1.25) saturate(1.4)';
      } else if (isMidTier) {
        brightnessFilter = 'brightness(1.17) saturate(1.2)';
      }

      const shinyStyle = {
        fontWeight: 'bold',
        filter: brightnessFilter,
        textShadow: `
          0 0 10px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${glowIntensity}),
          0 0 20px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${glowIntensity * 0.75}),
          0 0 30px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${glowIntensity * 0.5})
          ${extraLayers}
        `
      };
      return wrapWithVfx(
        <span className="shiny-player-name" style={shinyStyle}>
          {emojiEl} {playerName} {emojiEl}{moneyBag}
        </span>
      );
    }
    // Plain item with emoji — still apply VFX if present
    return wrapWithVfx(<span>{emojiEl} {playerName} {emojiEl}{moneyBag}</span>);
  }
  return <span>{playerName}{moneyBag}</span>;
}

export default PlayerNameWithEmoji;
